import { useEffect, useState } from "react";
import { Coins, Plus, FileText, Download, Loader2, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFideicomiso } from "../lib/fideicomiso";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const ESTADO_BADGE = {
    devengado: "badge-gold",
    facturado: "badge-gold",
    cobrado: "badge-exito",
    parcialmente_cobrado: "badge-gold",
    vencido: "badge-error",
    cancelado: "badge-mock",
};

function fmt(n, cur = "MXN") {
    return (n || 0).toLocaleString("es-MX", { style: "currency", currency: cur });
}

export default function Honorarios() {
    const { selected } = useFideicomiso();
    const [tab, setTab] = useState("honorarios");
    const [honorarios, setHonorarios] = useState([]);
    const [tarifas, setTarifas] = useState([]);
    const [busy, setBusy] = useState(false);
    const [openCobro, setOpenCobro] = useState(null);
    const [cobroForm, setCobroForm] = useState({ monto: 0, fecha_cobro: "" });

    const load = async () => {
        if (!selected) return;
        const [{ data: h }, { data: t }] = await Promise.all([
            api.get(`/honorarios?fideicomiso_id=${selected.id}`),
            api.get(`/tarifas?fideicomiso_id=${selected.id}`),
        ]);
        setHonorarios(h); setTarifas(t);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

    const devengar = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/honorarios/devengar",
                { fideicomiso_id: selected.id });
            toast.success(`Devengados: ${data.devengados}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const facturar = async (id) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/honorarios/${id}/facturar`, {});
            toast.success(`CFDI mock ${data.folio_interno}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const cobrar = async (h) => {
        try {
            await api.post(`/honorarios/${h.id}/cobrar`, {
                monto: parseFloat(cobroForm.monto),
                fecha_cobro: cobroForm.fecha_cobro,
            });
            toast.success("Cobro registrado");
            setOpenCobro(null); setCobroForm({ monto: 0, fecha_cobro: "" });
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };

    const descargarCFDI = async (cfdiId, tipo) => {
        try {
            const r = await api.get(`/cfdi/${cfdiId}/${tipo}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `cfdi-${cfdiId.slice(0, 8)}.${tipo}`; a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error("Error descargando"); }
    };

    if (!selected) return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso.</div>;

    const totalPorCobrar = honorarios
        .filter((h) => ["facturado", "vencido", "parcialmente_cobrado"].includes(h.estado))
        .reduce((s, h) => s + (h.total - (h.total_pagado || 0)), 0);

    return (
        <div className="p-8 lg:p-10 max-w-[1500px] space-y-6" data-testid="honorarios-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Núcleo · {selected.numero_interno}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Coins className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Honorarios
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Devengo · facturación · <span className="badge-mock">CFDI MOCK</span> · cobro
                    </p>
                </div>
                <div className="flex items-baseline gap-4">
                    <div>
                        <div className="text-[10px] uppercase text-muted-foreground">Por cobrar</div>
                        <div className="text-2xl font-display text-[color:var(--brand-coral)]">
                            {fmt(totalPorCobrar)}
                        </div>
                    </div>
                    <Button size="sm" onClick={devengar} disabled={busy}
                        data-testid="devengar-honorarios-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
                            (<><Plus className="h-4 w-4 mr-1" /> Devengar mes</>)}
                    </Button>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="honorarios">Honorarios</TabsTrigger>
                    <TabsTrigger value="tarifas">Tarifas</TabsTrigger>
                </TabsList>

                <TabsContent value="honorarios">
                    <div className="rounded-md border border-border bg-card overflow-hidden">
                        <table className="trf-table">
                            <thead><tr>
                                <th>Periodo</th><th>Concepto</th><th>Devengo</th>
                                <th className="text-right">Monto</th>
                                <th className="text-right">IVA</th>
                                <th className="text-right">Total</th>
                                <th className="text-right">Pagado</th>
                                <th>Estado</th><th>Vence</th><th>CFDI</th><th></th>
                            </tr></thead>
                            <tbody>
                                {honorarios.map((h) => (
                                    <tr key={h.id} data-testid={`honorario-row-${h.id}`}>
                                        <td className="tabular text-xs">{h.periodo}</td>
                                        <td className="text-xs">{h.concepto}</td>
                                        <td className="text-xs">
                                            <span className="badge-gold text-[10px]">{h.tipo_devengo}</span>
                                        </td>
                                        <td className="text-right tabular">{fmt(h.monto, h.moneda)}</td>
                                        <td className="text-right tabular text-muted-foreground">{fmt(h.iva, h.moneda)}</td>
                                        <td className="text-right tabular font-medium">{fmt(h.total, h.moneda)}</td>
                                        <td className="text-right tabular">{fmt(h.total_pagado || 0, h.moneda)}</td>
                                        <td><span className={ESTADO_BADGE[h.estado] || "badge-gold"}>{h.estado}</span></td>
                                        <td className="tabular text-[10px]">{h.fecha_vencimiento_pago}</td>
                                        <td>
                                            {h.cfdi_id ? (
                                                <div className="flex gap-1">
                                                    <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                                                        onClick={() => descargarCFDI(h.cfdi_id, "pdf")}
                                                        data-testid={`cfdi-pdf-${h.id}`}>
                                                        <Download className="h-3 w-3 mr-0.5" /> PDF
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                                                        onClick={() => descargarCFDI(h.cfdi_id, "xml")}
                                                        data-testid={`cfdi-xml-${h.id}`}>
                                                        XML
                                                    </Button>
                                                </div>
                                            ) : <span className="text-muted-foreground text-[10px]">—</span>}
                                        </td>
                                        <td className="flex gap-1">
                                            {h.estado === "devengado" && (
                                                <Button size="sm" variant="ghost"
                                                    onClick={() => facturar(h.id)}
                                                    data-testid={`facturar-honorario-${h.id}`}>
                                                    <FileText className="h-3 w-3 mr-1" /> Facturar
                                                </Button>
                                            )}
                                            {["facturado", "vencido", "parcialmente_cobrado"].includes(h.estado) && (
                                                <Dialog open={openCobro === h.id}
                                                    onOpenChange={(v) => setOpenCobro(v ? h.id : null)}>
                                                    <DialogTrigger asChild>
                                                        <Button size="sm" variant="ghost"
                                                            data-testid={`cobrar-honorario-${h.id}`}
                                                            onClick={() => setCobroForm({
                                                                monto: h.total - (h.total_pagado || 0),
                                                                fecha_cobro: new Date().toISOString().slice(0, 10),
                                                            })}>
                                                            <DollarSign className="h-3 w-3 mr-1" /> Cobrar
                                                        </Button>
                                                    </DialogTrigger>
                                                    <DialogContent>
                                                        <DialogHeader><DialogTitle>Registrar cobro</DialogTitle></DialogHeader>
                                                        <div className="space-y-3">
                                                            <div><Label>Monto ({h.moneda})</Label>
                                                                <Input type="number" step="0.01" value={cobroForm.monto}
                                                                    onChange={(e) => setCobroForm({ ...cobroForm, monto: e.target.value })} />
                                                            </div>
                                                            <div><Label>Fecha de cobro</Label>
                                                                <Input type="date" value={cobroForm.fecha_cobro}
                                                                    onChange={(e) => setCobroForm({ ...cobroForm, fecha_cobro: e.target.value })} />
                                                            </div>
                                                            <DialogFooter>
                                                                <Button onClick={() => cobrar(h)}>Registrar</Button>
                                                            </DialogFooter>
                                                        </div>
                                                    </DialogContent>
                                                </Dialog>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {honorarios.length === 0 && (
                                    <tr><td colSpan={11} className="text-center py-6 text-muted-foreground">Sin honorarios.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="tarifas">
                    <div className="space-y-3">
                        {tarifas.map((t) => (
                            <div key={t.id} className="kpi-card" data-testid={`tarifa-${t.id}`}>
                                <div className="flex items-baseline justify-between">
                                    <h3 className="font-display text-lg">{t.nombre}</h3>
                                    <span className={t.fideicomiso_id ? "badge-exito" : "badge-gold"}>
                                        {t.fideicomiso_id ? "asignada" : "base"}
                                    </span>
                                </div>
                                <div className="text-xs text-muted-foreground mb-2">
                                    Tipo: {t.tipo_fideicomiso} · vigente desde {t.vigente_desde}
                                </div>
                                <table className="trf-table text-xs">
                                    <thead><tr>
                                        <th>Código</th><th>Nombre</th><th>Devengo</th>
                                        <th>Disparador</th>
                                        <th className="text-right">Monto</th>
                                        <th>IVA</th>
                                    </tr></thead>
                                    <tbody>
                                        {(t.conceptos || []).map((c) => (
                                            <tr key={c.codigo}>
                                                <td className="tabular font-medium">{c.codigo}</td>
                                                <td>{c.nombre}</td>
                                                <td><span className="badge-gold text-[10px]">{c.tipo_devengo}</span></td>
                                                <td className="text-xs text-muted-foreground">{c.evento_disparador || "—"}</td>
                                                <td className="text-right tabular">{fmt(c.monto, c.moneda)}</td>
                                                <td>{c.iva_aplicable ? "16%" : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                        {tarifas.length === 0 && (
                            <div className="text-sm text-muted-foreground">Sin tarifas asignadas.</div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
